// Comments module for managing sidebar comments panel

import { getCurrentUser } from './auth.js'
import { getCurrentFilePath } from './files.js'
import { getEditor } from './editor.js'
import { apiPost, apiPut, apiDelete } from './api.js'

let currentComments = [];
let panelOpen = false;
let showResolved = false;
let currentSelection = null;
let currentlyHighlightedCommentId = null;

/**
 * Load comments for a CRD file
 * @param {string} crdFile - The file path (e.g., "backlog/Button_v1_CRD.md")
 */
export async function loadComments(crdFile) {
    try {
        const encodedPath = encodeURIComponent(crdFile);
        const response = await fetch(`/api/comments/${encodedPath}`, {
            credentials: 'include'
        });
        
        if (!response.ok) {
            throw new Error(`Failed to load comments: ${response.status}`);
        }
        
        const data = await response.json();
        currentComments = data.comments || [];
        
        // Update badge count
        updateCommentBadge();
        
        // Re-render panel if it's open
        if (panelOpen) {
            renderCommentsPanel();
        }
    } catch (error) {
        console.error('Error loading comments:', error);
        currentComments = [];
        updateCommentBadge();
    }
}

export async function restoreCommentHighlights() {
    const editor = getEditor();
    const crdFile = getCurrentFilePath();
    
    if (!editor || !crdFile) return;
    
    // Load comments if not already loaded
    if (currentComments.length === 0) {
        await loadComments(crdFile);
    }
    
    if (currentComments.length === 0) return;
    
    const doc = editor.state.doc;
    
    currentComments.forEach(comment => {
        if (!comment.highlighted_text) return;
        if (comment.resolved) return;  // Skip resolved comments
        
        const searchText = comment.highlighted_text;
        let found = false;
        
        // Traverse all text nodes to find the exact position
        doc.descendants((node, pos) => {
            if (found || !node.isText) return true;
            
            const nodeText = node.text;
            let searchIndex = 0;
            
            // Search for all occurrences within this node
            while (searchIndex < nodeText.length) {
                const index = nodeText.indexOf(searchText, searchIndex);
                
                if (index !== -1) {
                    // pos is the position before the text node starts
                    // We need pos + index for the start of the match
                    const from = pos + index;
                    const to = from + searchText.length;
                    
                    console.log('Applying mark for comment', comment.id, 'from', from, 'to', to);
                    
                    try {
                        editor.chain()
                            .setTextSelection({ from, to })
                            .setMark('comment', { commentId: comment.id })
                            .run();
                        found = true;
                        return false; // Stop traversal
                    } catch (e) {
                        console.error('Error applying mark:', e);
                    }
                }
                
                searchIndex = index === -1 ? nodeText.length : index + 1;
            }
            
            return true; // Continue traversal
        });
        
        if (!found) {
            console.log('Could not find text for comment:', comment.id);
        }
    });
    
    // Clear selection after restoring highlights
    editor.commands.setTextSelection(0);
}

/**
 * Get count of open (unresolved) comments
 */
export function getOpenCommentCount() {
    return currentComments.filter(c => !c.resolved).length;
}

/**
 * Update the comment badge in the nav bar
 */
function updateCommentBadge() {
    const badge = document.getElementById('comments-toggle');
    if (!badge) return;
    
    const count = getOpenCommentCount();
    
    if (count > 0) {
        badge.innerHTML = count;
        badge.classList.add('has-comments');
    } else {
        badge.innerHTML = '<i class="ph ph-chat-circle"></i>';
        badge.classList.remove('has-comments');
    }
    badge.style.display = ''; // Always show
    
    // Also show history button when comments button is shown
    const historyBtn = document.getElementById('history-toggle-btn');
    if (historyBtn) {
        historyBtn.style.display = '';
    }
}

/**
 * Refresh comment count badges in the file explorer
 */
async function refreshExplorerCommentBadges() {
    // Find all folder sections and check which ones have visible (expanded) file lists
    const folderSections = document.querySelectorAll('.folder-section');
    
    for (const section of folderSections) {
        const filesDiv = section.querySelector('.folder-files');
        const folder = section.dataset.folder;
        
        // Only process expanded folders (where filesDiv is visible)
        if (!filesDiv || filesDiv.style.display !== 'block' || !folder) {
            continue;
        }
        
        try {
            const countsRes = await fetch(`/api/comments/counts/${folder}`, {
                credentials: 'include'
            });
            
            if (countsRes.ok) {
                const commentCounts = await countsRes.json();
                
                // Update each file item's badge
                filesDiv.querySelectorAll('.file-item').forEach(item => {
                    const filename = item.dataset.name;
                    const count = commentCounts[filename] || 0;
                    
                    let badge = item.querySelector('.file-comment-badge');
                    
                    if (count > 0) {
                        if (!badge) {
                            badge = document.createElement('span');
                            badge.className = 'file-comment-badge';
                            item.appendChild(badge);
                        }
                        badge.textContent = count;
                    } else if (badge) {
                        badge.remove();
                    }
                });
            }
        } catch (err) {
            console.error('Failed to refresh comment badges for folder:', folder, err);
        }
    }
}

/**
 * Open the comments panel
 */
export async function openCommentsPanel() {
    // Close history panel if open
    if (typeof closeHistoryPanel === 'function') {
        closeHistoryPanel();
    }
    
    // Close file explorer if open to create space
    const sidebar = document.querySelector('.sidebar');
    if (sidebar && !sidebar.classList.contains('collapsed')) {
        sidebar.classList.add('collapsed');
        
        // Update explorer toggle button to reflect closed state
        const explorerBtn = document.getElementById('openExplorerBtn');
        if (explorerBtn) {
            explorerBtn.classList.remove('active');
            const btnText = explorerBtn.querySelector('span');
            if (btnText) btnText.textContent = 'Open explorer';
        }
    }
    
    const panel = document.getElementById('comments-panel');
    if (!panel) return;
    
    panelOpen = true;
    panel.classList.add('open');
    
    // Add class to editor container to shrink it
    const editorContainer = document.getElementById('editorContainer');
    if (editorContainer) {
        editorContainer.classList.add('comments-open');
    }
    
    // Add class to editor area to trigger flex change
    const editorArea = document.querySelector('.editor-area');
    if (editorArea) {
        editorArea.classList.add('comments-open');
    }
    
    const crdFile = getCurrentFilePath();
    if (crdFile) {
        await loadComments(crdFile);
        restoreCommentHighlights();
    }
    
    renderCommentsPanel();
}

/**
 * Close the comments panel
 */
export function closeCommentsPanel() {
    const panel = document.getElementById('comments-panel');
    if (!panel) return;
    
    panelOpen = false;
    panel.classList.remove('open');
    
    // Remove class from editor container to restore its size
    const editorContainer = document.getElementById('editorContainer');
    if (editorContainer) {
        editorContainer.classList.remove('comments-open');
    }
    
    // Remove class from editor area
    const editorArea = document.querySelector('.editor-area');
    if (editorArea) {
        editorArea.classList.remove('comments-open');
    }
}

/**
 * Toggle the comments panel
 */
export function toggleCommentsPanel() {
    if (panelOpen) {
        closeCommentsPanel();
    } else {
        openCommentsPanel();
    }
}

/**
 * Clear comments state
 */
export function clearComments() {
    currentComments = [];
    updateCommentBadge();
    if (panelOpen) {
        renderCommentsPanel();
    }
}

/**
 * Format time ago string
 */
function timeAgo(dateString) {
    const date = new Date(dateString);
    const seconds = Math.floor((new Date() - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Truncate text to a maximum length
 */
function truncateText(text, maxLength) {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
}

/**
 * Group comments by section
 */
function groupCommentsBySection() {
    const grouped = {};
    const orphaned = [];
    
    currentComments.forEach(comment => {
        if (comment.section) {
            if (!grouped[comment.section]) {
                grouped[comment.section] = [];
            }
            grouped[comment.section].push(comment);
        } else {
            orphaned.push(comment);
        }
    });
    
    return { grouped, orphaned };
}

/**
 * Render a single comment card
 */
function renderCommentCard(comment) {
    const isResolved = comment.resolved === 1;
    const replyCount = comment.replies?.length || 0;
    
    // Filter out resolved comments if showResolved is false
    if (isResolved && !showResolved) {
        return '';
    }
    
    const currentUser = getCurrentUser();
    
    return `
        <div class="comment-card ${isResolved ? 'resolved' : ''}" data-comment-id="${escapeHtml(comment.id)}">
            <div class="comment-card-highlight">
                "${escapeHtml(truncateText(comment.highlighted_text, 75))}"
            </div>
            <div class="comment-meta">
                <span class="comment-author">${escapeHtml(comment.author_name || 'Unknown')}</span>
                <span class="comment-time">${timeAgo(comment.created_at)}</span>
                ${isResolved ? '<span class="comment-resolved-badge">Resolved</span>' : ''}
            </div>
            <div class="comment-content">
                ${escapeHtml(comment.content)}
            </div>
            ${replyCount > 0 ? `
                <div class="comment-replies">
                    ${comment.replies.map(reply => `
                        <div class="comment-reply" data-reply-id="${escapeHtml(reply.id)}">
                            <div class="reply-header">
                                <div class="reply-meta">
                                    <span class="reply-author">${escapeHtml(reply.author_name || 'Unknown')}</span>
                                    <span class="reply-time">${timeAgo(reply.created_at)}</span>
                                </div>
                                <div class="reply-action-icons">
                                    ${currentUser && currentUser.id === reply.author_id ? `
                                        <button class="icon-btn icon-btn-sm edit-reply-btn" data-reply-id="${escapeHtml(reply.id)}" data-comment-id="${escapeHtml(comment.id)}" title="Edit">
                                            <i class="ph ph-pencil-simple"></i>
                                        </button>
                                    ` : ''}
                                    ${currentUser && (currentUser.id === reply.author_id || currentUser.role === 'repo_owner') ? `
                                        <button class="icon-btn icon-btn-sm delete-reply-btn" data-reply-id="${escapeHtml(reply.id)}" title="Delete">
                                            <i class="ph ph-trash"></i>
                                        </button>
                                    ` : ''}
                                </div>
                            </div>
                            <div class="reply-content">${escapeHtml(reply.content)}</div>
                        </div>
                    `).join('')}
                </div>
            ` : ''}
            <div class="comment-actions">
                <button class="comment-action-btn reply-btn" data-comment-id="${escapeHtml(comment.id)}">
                    <i class="ph ph-chat"></i> Reply
                </button>
                <div class="comment-action-icons">
                    <button class="icon-btn resolve-btn" data-comment-id="${escapeHtml(comment.id)}" title="${isResolved ? 'Unresolve' : 'Resolve'}">
                        <i class="ph ph-${isResolved ? 'arrow-counter-clockwise' : 'check'}"></i>
                    </button>
                    ${currentUser && currentUser.id === comment.author_id ? `
                        <button class="icon-btn edit-comment-btn" data-comment-id="${escapeHtml(comment.id)}" title="Edit">
                            <i class="ph ph-pencil-simple"></i>
                        </button>
                    ` : ''}
                    ${currentUser && (currentUser.id === comment.author_id || currentUser.role === 'repo_owner') ? `
                        <button class="icon-btn delete-comment-btn" data-comment-id="${escapeHtml(comment.id)}" title="Delete">
                            <i class="ph ph-trash"></i>
                        </button>
                    ` : ''}
                </div>
            </div>
        </div>
    `;
}

/**
 * Handle resolve/unresolve button click
 */
function removeCommentHighlight(commentId) {
    const editor = getEditor();
    if (!editor) return;
    
    // Find and remove the mark
    const { doc } = editor.state;
    
    doc.descendants((node, pos) => {
        if (!node.isText) return true;
        
        const marks = node.marks || [];
        const commentMark = marks.find(m => m.type.name === 'comment' && m.attrs.commentId === commentId);
        
        if (commentMark) {
            const from = pos;
            const to = pos + node.nodeSize;
            editor.chain().setTextSelection({ from, to }).unsetMark('comment').run();
            return false;
        }
        
        return true;
    });
    
    editor.commands.setTextSelection(0);
}

/**
 * Handle edit comment
 */
async function handleEditComment(commentId) {
    const comment = currentComments.find(c => c.id === commentId);
    if (!comment) return;
    
    const card = document.querySelector(`.comment-card[data-comment-id="${commentId}"]`);
    if (!card) return;
    
    const contentDiv = card.querySelector('.comment-content');
    const originalContent = comment.content;
    
    // Replace content with textarea
    contentDiv.innerHTML = `
        <textarea class="edit-comment-input">${escapeHtml(originalContent)}</textarea>
        <div class="edit-actions">
            <button class="btn-secondary btn-sm cancel-edit-btn">Cancel</button>
            <button class="btn-primary btn-sm save-edit-btn">Save</button>
        </div>
    `;
    
    const textarea = contentDiv.querySelector('.edit-comment-input');
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    
    contentDiv.querySelector('.cancel-edit-btn').addEventListener('click', () => {
        contentDiv.innerHTML = escapeHtml(originalContent);
    });
    
    contentDiv.querySelector('.save-edit-btn').addEventListener('click', async () => {
        const newContent = textarea.value.trim();
        if (!newContent) return;
        
        try {
            const response = await apiPut(`/api/comments/${commentId}`, { content: newContent });
            
            if (!response.ok) throw new Error('Failed to update comment');
            
            const crdFile = getCurrentFilePath();
            if (crdFile) await loadComments(crdFile);
        } catch (error) {
            console.error('Error updating comment:', error);
            alert('Failed to update comment');
        }
    });
}

/**
 * Handle delete comment
 */
async function handleDeleteComment(commentId) {
    if (!confirm('Are you sure you want to delete this comment? This cannot be undone.')) {
        return;
    }
    
    try {
        // Remove highlight first
        removeCommentHighlight(commentId);
        
        const response = await apiDelete(`/api/comments/${commentId}`);
        
        if (!response.ok) throw new Error('Failed to delete comment');
        
        const crdFile = getCurrentFilePath();
        if (crdFile) await loadComments(crdFile);
        
        refreshExplorerCommentBadges();
    } catch (error) {
        console.error('Error deleting comment:', error);
        alert('Failed to delete comment');
    }
}

/**
 * Handle edit reply
 */
async function handleEditReply(replyId, commentId) {
    const comment = currentComments.find(c => c.id === commentId);
    if (!comment || !comment.replies) return;
    
    const reply = comment.replies.find(r => r.id === replyId);
    if (!reply) return;
    
    const replyDiv = document.querySelector(`.comment-reply[data-reply-id="${replyId}"]`);
    if (!replyDiv) return;
    
    const contentDiv = replyDiv.querySelector('.reply-content');
    const originalContent = reply.content;
    
    contentDiv.innerHTML = `
        <textarea class="edit-reply-input">${escapeHtml(originalContent)}</textarea>
        <div class="edit-actions">
            <button class="btn-secondary btn-sm cancel-edit-btn">Cancel</button>
            <button class="btn-primary btn-sm save-edit-btn">Save</button>
        </div>
    `;
    
    const textarea = contentDiv.querySelector('.edit-reply-input');
    textarea.focus();
    
    contentDiv.querySelector('.cancel-edit-btn').addEventListener('click', () => {
        contentDiv.innerHTML = escapeHtml(originalContent);
    });
    
    contentDiv.querySelector('.save-edit-btn').addEventListener('click', async () => {
        const newContent = textarea.value.trim();
        if (!newContent) return;
        
        try {
            const response = await apiPut(`/api/replies/${replyId}`, { content: newContent });
            
            if (!response.ok) throw new Error('Failed to update reply');
            
            const crdFile = getCurrentFilePath();
            if (crdFile) await loadComments(crdFile);
        } catch (error) {
            console.error('Error updating reply:', error);
            alert('Failed to update reply');
        }
    });
}

/**
 * Handle delete reply
 */
async function handleDeleteReply(replyId) {
    if (!confirm('Are you sure you want to delete this reply?')) {
        return;
    }
    
    try {
        const response = await apiDelete(`/api/replies/${replyId}`);
        
        if (!response.ok) throw new Error('Failed to delete reply');
        
        const crdFile = getCurrentFilePath();
        if (crdFile) await loadComments(crdFile);
    } catch (error) {
        console.error('Error deleting reply:', error);
        alert('Failed to delete reply');
    }
}

async function handleResolveClick(commentId) {
    console.log('Resolve clicked, commentId:', commentId);
    
    const comment = currentComments.find(c => c.id === commentId);
    console.log('Found comment:', comment);
    
    if (!comment) {
        console.error('Comment not found in currentComments');
        return;
    }
    
    const newResolved = comment.resolved ? 0 : 1;
    const url = `/api/comments/${commentId}/resolve`;
    console.log('Calling:', url, 'with resolved:', newResolved);
    
    try {
        const response = await apiPut(url, { resolved: newResolved });
        
        console.log('Response status:', response.status);
        
        if (!response.ok) {
            const errorText = await response.text();
            console.error('Error response:', errorText);
            throw new Error('Failed to update comment');
        }
        
        // If resolving, remove highlight immediately
        if (newResolved === 1) {
            removeCommentHighlight(commentId);
        }
        
        // Reload comments first
        const crdFile = getCurrentFilePath();
        if (crdFile) await loadComments(crdFile);
        
        // THEN restore highlights (now currentComments has updated resolved state)
        if (newResolved === 0) {
            restoreCommentHighlights();
        }
        
        // Update explorer badges
        refreshExplorerCommentBadges();
        
    } catch (error) {
        console.error('Error resolving comment:', error);
        alert('Failed to update comment');
    }
}

/**
 * Show reply input for a comment
 */
function showReplyInput(commentId) {
    // Remove any existing reply input
    document.querySelectorAll('.reply-input-container').forEach(el => el.remove());
    
    const card = document.querySelector(`.comment-card[data-comment-id="${commentId}"]`);
    if (!card) return;
    
    const container = document.createElement('div');
    container.className = 'reply-input-container';
    container.innerHTML = `
        <textarea class="reply-input" placeholder="Write a reply..." rows="2"></textarea>
        <div class="reply-input-actions">
            <button class="btn-secondary btn-sm cancel-reply-btn">Cancel</button>
            <button class="btn-primary btn-sm submit-reply-btn" data-comment-id="${commentId}">Reply</button>
        </div>
    `;
    
    card.appendChild(container);
    container.querySelector('.reply-input').focus();
    
    container.querySelector('.cancel-reply-btn').addEventListener('click', () => {
        container.remove();
    });
    
    container.querySelector('.submit-reply-btn').addEventListener('click', () => {
        submitReply(commentId, container.querySelector('.reply-input').value);
    });
}

/**
 * Submit a reply to a comment
 */
async function submitReply(commentId, content) {
    if (!content.trim()) return;
    
    try {
        const response = await apiPost(`/api/comments/${commentId}/replies`, { content: content.trim() });
        
        if (!response.ok) throw new Error('Failed to post reply');
        
        // Reload comments
        const crdFile = getCurrentFilePath();
        if (crdFile) await loadComments(crdFile);
        
    } catch (error) {
        console.error('Error posting reply:', error);
        alert('Failed to post reply');
    }
}

/**
 * Render the comments panel content
 */
export function renderCommentsPanel() {
    const content = document.getElementById('comments-content');
    if (!content) return;
    
    if (currentComments.length === 0) {
        content.innerHTML = '<div class="empty-comments">No comments yet</div>';
        return;
    }
    
    const { grouped, orphaned } = groupCommentsBySection();
    
    let html = '';
    
    // Render grouped comments by section
    Object.keys(grouped).sort().forEach(section => {
        const comments = grouped[section];
        const visibleComments = comments.filter(c => showResolved || !c.resolved);
        
        if (visibleComments.length === 0) return;
        
        html += `
            <div class="comment-section-group">
                <div class="comment-section-header">${escapeHtml(section)}</div>
                ${comments.map(renderCommentCard).join('')}
            </div>
        `;
    });
    
    // Render orphaned comments
    if (orphaned.length > 0) {
        const visibleOrphaned = orphaned.filter(c => showResolved || !c.resolved);
        
        if (visibleOrphaned.length > 0) {
            html += `
                <div class="comment-section-group orphaned">
                    <div class="comment-section-header orphaned-header">
                        <i class="ph-bold ph-warning"></i> Orphaned comments
                    </div>
                    ${orphaned.map(renderCommentCard).join('')}
                </div>
            `;
        }
    }
    
    content.innerHTML = html || '<div class="empty-comments">No comments to display</div>';
    
    // Add event listeners for action buttons
    content.querySelectorAll('.resolve-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            handleResolveClick(btn.dataset.commentId);
        });
    });
    
    content.querySelectorAll('.reply-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            showReplyInput(btn.dataset.commentId);
        });
    });
    
    // Edit comment buttons
    content.querySelectorAll('.edit-comment-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            handleEditComment(btn.dataset.commentId);
        });
    });
    
    // Delete comment buttons
    content.querySelectorAll('.delete-comment-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            handleDeleteComment(btn.dataset.commentId);
        });
    });
    
    // Edit reply buttons
    content.querySelectorAll('.edit-reply-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            handleEditReply(btn.dataset.replyId, btn.dataset.commentId);
        });
    });
    
    // Delete reply buttons
    content.querySelectorAll('.delete-reply-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            handleDeleteReply(btn.dataset.replyId);
        });
    });
    
    // Click comment card to scroll to highlight in editor
    content.querySelectorAll('.comment-card').forEach(card => {
        card.addEventListener('click', (e) => {
            // Don't trigger if clicking on a button
            if (e.target.closest('button') || e.target.closest('.reply-input-container')) return;
            
            const commentId = card.dataset.commentId;
            
            // Highlight this card
            highlightCommentCard(commentId);
            
            // Scroll editor to the comment
            scrollEditorToComment(commentId);
        });
    });
}

/**
 * Setup editor selection listener to detect cursor in comment highlights
 */
function setupEditorSelectionListener() {
    console.log('🔧 Setting up editor selection listener');
    const editor = getEditor();
    console.log('🔧 Editor:', editor ? 'found' : 'NOT FOUND');
    if (!editor) return;
    
    editor.on('selectionUpdate', ({ editor }) => {
        console.log('👆 Selection update fired');
        const { from } = editor.state.selection;
        
        // Check if cursor is inside a comment mark
        const $pos = editor.state.doc.resolve(from);
        const marks = $pos.marks();
        console.log('👆 Marks at position:', marks.map(m => m.type.name));
        
        const commentMark = marks.find(m => m.type.name === 'comment');
        
        if (commentMark) {
            console.log('👆 Found comment mark:', commentMark.attrs.commentId);
            const commentId = commentMark.attrs.commentId;
            openCommentsPanel();
            highlightCommentCard(commentId);
        }
    });
}

function highlightCommentCard(commentId) {
    // Clear previous highlight
    document.querySelectorAll('.comment-card.highlighted').forEach(c => {
        c.classList.remove('highlighted');
    });
    
    const card = document.querySelector(`.comment-card[data-comment-id="${commentId}"]`);
    if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        card.classList.add('highlighted');
        currentlyHighlightedCommentId = commentId;
    }
}

function clearCommentHighlight() {
    document.querySelectorAll('.comment-card.highlighted').forEach(c => {
        c.classList.remove('highlighted');
    });
    currentlyHighlightedCommentId = null;
}

function scrollEditorToComment(commentId) {
    const editor = getEditor();
    if (!editor) return;
    
    // Find the mark in the document
    let targetPos = null;
    
    editor.state.doc.descendants((node, pos) => {
        if (targetPos !== null) return false;
        
        const marks = node.marks || [];
        const commentMark = marks.find(m => m.type.name === 'comment' && m.attrs.commentId === commentId);
        
        if (commentMark) {
            targetPos = pos;
            return false;
        }
        
        return true;
    });
    
    if (targetPos !== null) {
        // Set cursor to that position
        editor.commands.setTextSelection(targetPos);
        
        // Scroll the editor to show that position
        const editorElement = document.querySelector('.ProseMirror');
        if (editorElement) {
            // Find the highlight element
            const highlightEl = editorElement.querySelector(`span.comment-highlight[data-comment-id="${commentId}"]`);
            if (highlightEl) {
                highlightEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }
}

/**
 * Handle click on comment highlight in editor
 */
function handleHighlightClick(e) {
    console.log('🖱️ handleHighlightClick fired');
    console.log('🖱️ e.target:', e.target);
    console.log('🖱️ e.target.tagName:', e.target.tagName);
    console.log('🖱️ e.target.className:', e.target.className);
    
    const highlight = e.target.closest('.comment-highlight');
    console.log('🖱️ Found highlight element:', highlight);
    
    if (!highlight) {
        console.log('🖱️ No .comment-highlight found, returning');
        return;
    }
    
    const commentId = highlight.getAttribute('data-comment-id');
    console.log('🖱️ Comment ID:', commentId);
    
    if (!commentId) return;
    
    const comment = currentComments.find(c => c.id === commentId);
    console.log('🖱️ Found comment:', comment);
    
    if (!comment) return;
    
    console.log('🖱️ Opening panel and highlighting');
    
    if (!panelOpen) {
        openCommentsPanel();
    }
    
    setTimeout(() => {
        highlightCommentCard(commentId);
    }, 150);
}

/**
 * Initialize comments panel event listeners
 */
export function initCommentsPanel() {
    // Toggle button
    const toggleBtn = document.getElementById('comments-toggle');
    if (toggleBtn) {
        toggleBtn.addEventListener('click', toggleCommentsPanel);
    }
    
    // Show resolved checkbox
    const showResolvedCheckbox = document.getElementById('show-resolved');
    if (showResolvedCheckbox) {
        showResolvedCheckbox.addEventListener('change', (e) => {
            showResolved = e.target.checked;
            renderCommentsPanel();
        });
    }
    
    // Click on highlighted text in editor - use event delegation since ProseMirror 
    // doesn't exist until a file is loaded
    const editorArea = document.getElementById('editorArea');
    if (editorArea) {
        editorArea.addEventListener('click', handleHighlightClick);
        console.log('🔧 Click handler attached to editorArea via event delegation');
    }
    
    // Setup editor selection listener - delay to ensure editor is ready
    setTimeout(() => {
        setupEditorSelectionListener();
    }, 1000);
}

/**
 * Show comment creation popover
 */
export function showCommentPopover(selectedText, from, to) {
    currentSelection = { text: selectedText, from, to };
    
    // Remove any existing popover
    const existing = document.getElementById('comment-popover');
    if (existing) existing.remove();
    
    const popover = document.createElement('div');
    popover.id = 'comment-popover';
    popover.className = 'comment-popover';
    popover.innerHTML = `
        <div class="comment-popover-header">
            <span>Add comment</span>
            <button class="comment-popover-close" id="comment-popover-cancel">
                <i class="ph-bold ph-x"></i>
            </button>
        </div>
        <div class="comment-popover-body">
            <textarea id="comment-popover-input" placeholder="Write your comment..." rows="5"></textarea>
        </div>
        <div class="comment-popover-actions">
            <button class="btn-primary" id="comment-popover-save">Add comment</button>
        </div>
    `;
    
    document.body.appendChild(popover);
    
    // Position near the actual text selection
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        
        // Position below the selection, centered horizontally
        let top = rect.bottom + 8;
        let left = rect.left + (rect.width / 2) - 160;
        
        // Keep within viewport
        const popoverWidth = 320;
        const popoverHeight = 200;
        
        if (left < 10) left = 10;
        if (left + popoverWidth > window.innerWidth - 10) {
            left = window.innerWidth - popoverWidth - 10;
        }
        if (top + popoverHeight > window.innerHeight - 10) {
            // Show above the selection instead
            top = rect.top - popoverHeight - 8;
        }
        
        popover.style.position = 'fixed';
        popover.style.top = top + 'px';
        popover.style.left = left + 'px';
    }
    
    // Focus the textarea
    document.getElementById('comment-popover-input').focus();
    
    // Event handlers
    document.getElementById('comment-popover-cancel').addEventListener('click', hideCommentPopover);
    document.getElementById('comment-popover-save').addEventListener('click', saveNewComment);
    
    // Close on escape
    document.addEventListener('keydown', handlePopoverEscape);
}

/**
 * Handle escape key to close popover
 */
function handlePopoverEscape(e) {
    if (e.key === 'Escape') {
        hideCommentPopover();
    }
}

/**
 * Hide the comment popover
 */
function hideCommentPopover() {
    const popover = document.getElementById('comment-popover');
    if (popover) popover.remove();
    currentSelection = null;
    document.removeEventListener('keydown', handlePopoverEscape);
}

/**
 * Save a new comment
 */
async function saveNewComment() {
    const input = document.getElementById('comment-popover-input');
    const content = input?.value.trim();
    
    if (!content) {
        alert('Please enter a comment');
        return;
    }
    
    if (!currentSelection) {
        console.error('No selection data');
        return;
    }
    
    const crdFile = getCurrentFilePath();
    if (!crdFile) {
        alert('No file is currently open');
        return;
    }
    
    // Detect which section the selection is in
    const section = detectSection(currentSelection.from);
    
    try {
        const response = await apiPost('/api/comments', {
            crdFile,
            highlightedText: currentSelection.text,
            section,
            content
        });
        
        if (!response.ok) {
            throw new Error(`Failed to create comment: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Apply the highlight mark in the editor
        const editor = getEditor();
        if (editor && data.id) {
            editor.chain()
                .focus()
                .setTextSelection({ from: currentSelection.from, to: currentSelection.to })
                .setMark('comment', { commentId: data.id })
                .run();
        }
        
        // Reload comments to update the panel
        await loadComments(crdFile);
        
        // Update explorer badges
        refreshExplorerCommentBadges();
        
        hideCommentPopover();
        
    } catch (error) {
        console.error('Error saving comment:', error);
        alert('Failed to save comment');
    }
}

/**
 * Detect which section a position is in based on headings
 */
function detectSection(pos) {
    const editor = getEditor();
    if (!editor) return null;
    
    let section = null;
    
    editor.state.doc.nodesBetween(0, pos, (node) => {
        if (node.type.name === 'heading') {
            section = node.textContent;
        }
    });
    
    return section;
}

// Export functions to window for cross-module access
window.openCommentsPanel = openCommentsPanel;
window.closeCommentsPanel = closeCommentsPanel;
window.loadComments = loadComments;
window.highlightCommentCard = highlightCommentCard;
window.initCommentsPanel = initCommentsPanel;
window.refreshExplorerCommentBadges = refreshExplorerCommentBadges;
